"use client";
import "./Dashboard.scss";
import "../assets/scss/_app.scss";

import { Suspense, useContext, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { UserRole } from "../models";
import Wrapper from "../components/Wrapper";
import Sidebar from "../components/sidebar/Sidebar";
import Main from "../components/Main";
import Navbar from "../components/Navbar";
import Content from "../components/Content";
import { Footer } from "../components/Footer";
import Loader from "../components/Loader";
import { SUPPORTED_BROWSERS } from "../components/notification/DashboardNotificationManagerConstants";
import { isMacSafariWebView } from "../components/notification/NotificationBarUtil";
import { browserName } from "react-device-detect";
import { ModalContext } from "../routes/ModalContext";
import { useSessionState } from "../hooks/useSessionState";
import { tosLastUpdated } from "../utility/TermsOfServiceLastUpdated";
import { IModalProps } from "../components/CDataModal";
import { ErrorBoundary } from "react-error-boundary";
import {
  GenericErrorPage,
  logBoundaryErrorToDatadog,
} from "../pages/auth/GenericErrorPage";
import { DashboardNotificationManager } from "../components/notification/DashboardNotificationManager";
import { useAppSelector } from "../redux/hooks";
import { TermsOfServiceModal } from "./TermsOfServiceModal";
import { store } from "src/redux/store";

export interface IModalFunctions {
  showErrorModal: (text: string, error: any) => void;
  showTimeoutModal: () => void;
  setModal: (modal: IModalProps) => void;
  toggleModal: () => void;
  showUnsupportedBrowser?: () => void;
}

export const Dashboard = () => {
  const navigate = useNavigate();
  const user = useAppSelector((s) => s.user);
  const isOemAdmin = user?.role === UserRole.OEMAdmin;
  const [loading, setLoading] = useState<boolean>(true);
  const [showTOSModal, setShowTOSModal] = useState<boolean>(false);

  const modalContext = useContext(ModalContext);
  const sessionState = useSessionState();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await sessionState.initializeSessionState();
      const { accountInfo } = store.getState();

      if (!accountInfo!.isSetupFinished) {
        navigate("/initial-setup#select-initial-connection");
      }

      displayInitialUnsupportedBrowser();

      if (
        accountInfo?.tosVersion == null ||
        accountInfo!.tosVersion! < tosLastUpdated
      ) {
        setShowTOSModal(true);
      }

      setLoading(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const displayInitialUnsupportedBrowser = () => {
    if (
      !SUPPORTED_BROWSERS.includes(browserName) &&
      !isMacSafariWebView(navigator.userAgent)
    ) {
      modalContext.showUnsupportedBrowser();
    }
  };

  const renderDashboard = () => {
    return (
      <>
        <Navbar />
        <Wrapper>
          {/* OEM admins currently do not get a sidebar, this will change if they ever have more than 1 page they can see. */}
          {!isOemAdmin && <Sidebar />}
          <Main className="">
            <ErrorBoundary
              fallbackRender={(props) => (
                <GenericErrorPage
                  error={props.error}
                  isOutsideDashboard={false}
                  resetErrorBoundary={props.resetErrorBoundary}
                />
              )}
              onError={logBoundaryErrorToDatadog}
            >
              <DashboardNotificationManager />
              <Content>
                <Suspense fallback={<Loader />}>
                  <Outlet />
                </Suspense>
              </Content>
              <Footer />
              <TermsOfServiceModal
                displayed={showTOSModal}
                setDisplayed={setShowTOSModal}
              />
            </ErrorBoundary>
          </Main>
        </Wrapper>
      </>
    );
  };

  const contents = loading ? (
    <div className="text-center mt-4">
      <Loader />
    </div>
  ) : (
    renderDashboard()
  );

  return (
    <>
      <div className="p-0 layouts-dashboard">{contents}</div>
    </>
  );
};
